import { useQuery } from '@tanstack/react-query';
import { ErrorSummary } from '@loctopus/ui';
import { api } from '../../lib/api';
import { toParsedError } from '../../lib/errorDisplay';
import { formatMinor } from '../../lib/format';
import type {
  BillingPlanComponent,
  BillingPlanComponentType,
  CurrencyCode,
  FeePayer,
  TenantBillingPlan,
} from '../../lib/types';
import { Badge, Card, HelpPanel, PageHeader, Spinner } from '../../components/ui';

/**
 * Dashboard brief task 6 ("Billing page"): the tenant's OWN assigned plan in plain language - what
 * they pay, per ticket/order, who pays what - with every OTHER plan component type explicitly
 * listed as "Not used by your account" rather than omitted, so a non-technical owner sees the
 * full picture without being confused about what applies to them.
 *
 * Backed by the ASSUMED `GET /dashboard/billing-plan` route - see lib/api.ts's `billingPlan`
 * client and lib/types.ts's `TenantBillingPlan`/`BillingPlanComponent` doc comments for exactly
 * what's assumed (the route itself, `components[].feePayer`, and `issuesTaxInvoices`) and why.
 */
export function BillingPlanPage() {
  const planQuery = useQuery({ queryKey: ['billing-plan'], queryFn: api.billingPlan.get });

  return (
    <div>
      <PageHeader
        title="Billing"
        description="How Loctopus charges your account, in plain language."
      />
      <HelpPanel storageKey="help-billing-plan" title="What is this?">
        This page shows the plan Loctopus has set up for your account - what you're charged, on
        what, and whether that cost comes out of the buyer's payment or your own payout. Every
        other pricing option Loctopus offers is also listed below so you can see what does and
        doesn't apply to you. To change your plan, contact Loctopus - this page is read-only.
      </HelpPanel>
      {planQuery.isLoading ? (
        <Spinner />
      ) : planQuery.error ? (
        <ErrorSummary error={toParsedError(planQuery.error)} />
      ) : planQuery.data ? (
        <BillingPlanDetail plan={planQuery.data} />
      ) : null}
    </div>
  );
}

const COMPONENT_LABELS: Record<BillingPlanComponentType, string> = {
  per_transaction: 'Per-order fee',
  subscription: 'Monthly subscription',
  tiered_volume: 'Volume-based pricing',
  per_event: 'Per-event flat fee',
  community: 'Community / nonprofit rate',
  hybrid: 'Subscription + per-ticket fee',
  prepaid_credits: 'Prepaid credits',
  custom_deal: 'Custom negotiated deal',
  pass_through: 'Pass-through costs (e.g. SMS)',
};

const ALL_COMPONENT_TYPES = Object.keys(COMPONENT_LABELS) as BillingPlanComponentType[];

const FEE_PAYER_LABELS: Record<FeePayer, string> = {
  BUYER: 'the buyer pays this, added on top of their order',
  CLIENT: 'you pay this, deducted from your payout',
  SPLIT: 'split between you and the buyer',
};

function percent(bps?: number): string | null {
  if (bps === undefined) return null;
  return `${(bps / 100).toFixed(2)}%`;
}

/** Plain-English description of a single component's own numbers - deliberately doesn't try to
 * describe `tiered_volume`'s tier list field-by-field (too much detail for a non-technical
 * summary); it names the pricing shape and leaves exact tier breaks to Loctopus support. */
function describeComponent(c: BillingPlanComponent, currency: CurrencyCode): string {
  switch (c.type) {
    case 'per_transaction':
    case 'community': {
      const parts: string[] = [];
      if (c.percentBps !== undefined) parts.push(percent(c.percentBps)!);
      if (c.fixedMinor !== undefined) parts.push(formatMinor(c.fixedMinor, currency));
      return parts.length ? `${parts.join(' + ')} per order` : 'No per-order fee';
    }
    case 'subscription':
      return `${formatMinor(c.periodMinor ?? 0, currency)} per month, including ${c.includedVolume ?? 0} tickets` +
        (c.overageUnitMinor !== undefined
          ? `, then ${formatMinor(c.overageUnitMinor, currency)} per extra ticket`
          : '');
    case 'tiered_volume':
      return 'Price per ticket changes based on how many you sell - contact Loctopus for your exact tiers.';
    case 'per_event':
      return `${formatMinor(c.flatFeeMinor ?? 0, currency)} per event`;
    case 'hybrid':
      return `${formatMinor(c.periodMinor ?? 0, currency)} per month plus ${formatMinor(c.perTicketMinor ?? 0, currency)} per ticket`;
    case 'prepaid_credits':
      return 'Charged against a prepaid credit balance rather than per-order.';
    case 'custom_deal':
      return c.description ?? 'A custom rate negotiated directly with Loctopus.';
    case 'pass_through':
      return `Loctopus's own cost for ${c.item ?? 'this service'} is passed through to you` +
        (c.markupBps ? ` with a ${percent(c.markupBps)} markup` : ', at cost');
  }
}

function BillingPlanDetail({ plan }: { plan: TenantBillingPlan }) {
  const usedTypes = new Set(plan.components.map((c) => c.type));

  return (
    <div className="space-y-6">
      <Card title="Your plan">
        <dl className="grid grid-cols-2 gap-4 text-sm mb-2">
          <div>
            <dt className="text-slate-500">Plan name</dt>
            <dd className="font-medium text-slate-800">{plan.planName}</dd>
          </div>
          <div>
            <dt className="text-slate-500">Type</dt>
            <dd className="font-medium text-slate-800">
              {plan.isCustomDeal ? 'Custom deal, negotiated for your account' : 'Standard plan'}
            </dd>
          </div>
          <div>
            <dt className="text-slate-500">Who normally pays fees</dt>
            <dd className="font-medium text-slate-800">{FEE_PAYER_LABELS[plan.feePayerDefault]}</dd>
          </div>
          <div>
            <dt className="text-slate-500">Tax invoices</dt>
            <dd className="font-medium text-slate-800">
              {plan.issuesTaxInvoices
                ? 'Loctopus issues you a GST tax invoice for these fees.'
                : 'Loctopus does not issue a separate tax invoice for these fees.'}
            </dd>
          </div>
        </dl>
      </Card>

      <Card title="What you're charged">
        {plan.components.length === 0 ? (
          <p className="text-sm text-slate-500">
            Your plan has no per-order or recurring charges configured.
          </p>
        ) : (
          <ul className="space-y-3">
            {plan.components.map((c, i) => (
              <li key={`${c.type}-${i}`} className="border border-slate-200 rounded-brand p-3">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-medium text-slate-800">{COMPONENT_LABELS[c.type]}</span>
                  <Badge tone="brand">Used by your account</Badge>
                </div>
                <p className="text-sm text-slate-600">{describeComponent(c, plan.currency)}</p>
                <p className="text-xs text-slate-500 mt-1">
                  Who pays: {FEE_PAYER_LABELS[c.feePayer ?? plan.feePayerDefault]}
                  {c.feePayer === undefined && ' (plan default)'}
                </p>
                {c.description && <p className="text-xs text-slate-500 mt-1">{c.description}</p>}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card
        title="Other Loctopus pricing options"
        actions={<span className="text-xs text-slate-400">Not part of your plan</span>}
      >
        <p className="text-sm text-slate-500 mb-3">
          Loctopus offers other ways to charge for ticketing. None of these apply to your account
          - they're listed here only so you can see the full picture.
        </p>
        <ul className="space-y-2">
          {ALL_COMPONENT_TYPES.filter((t) => !usedTypes.has(t)).map((t) => (
            <li key={t} className="flex items-center justify-between gap-2 text-sm">
              <span className="text-slate-600">{COMPONENT_LABELS[t]}</span>
              <Badge tone="neutral">Not used by your account</Badge>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
